import type { CSSProperties, ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Glyph } from "./Glyph";
import { SkeletonText } from "./Skeleton";
import { FOCUS_RING } from "./focusRing.constants";
const SAFE_AREA_BOTTOM = "env(safe-area-inset-bottom, 0px)";
const BAR_STYLE: CSSProperties = {
  paddingBottom: `max(8px, ${SAFE_AREA_BOTTOM})`
};
export function AppBar({
  label,
  isHidden = false,
  style,
  className,
  children
}: {
  label: string;
  isHidden?: boolean;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  return <nav aria-label={label} aria-hidden={isHidden || undefined} inert={isHidden} style={{
    ...BAR_STYLE,
    ...style
  }} className={cn("fixed inset-x-0 bottom-0 flex justify-around bg-surface px-1.5 pt-2", "shadow-divider-t", "transition-[transform,opacity] duration-slow ease-out motion-reduce:transition-opacity", isHidden && "pointer-events-none translate-y-full opacity-0 motion-reduce:translate-y-0", className)} data-sentry-component="AppBar" data-sentry-source-file="AppBar.tsx">
            {children}
        </nav>;
}
export function AppBarItem({
  icon,
  label,
  isCurrent = false,
  onSelect,
  ...props
}: {
  icon: ReactNode;
  label: string;
  isCurrent?: boolean;
  onSelect: () => void;
  "aria-haspopup"?: "dialog";
  "aria-expanded"?: boolean;
}) {
  return <button type="button" onClick={onSelect} aria-current={isCurrent ? "page" : undefined} className={cn(APP_BAR_ITEM_CLASS, FOCUS_RING)} {...props} data-sentry-component="AppBarItem" data-sentry-source-file="AppBar.tsx">
            <span className={APP_BAR_ICON_CLASS}>{icon}</span>
            <span className={cn(APP_BAR_LABEL_CLASS, isCurrent ? "text-ink" : "text-ink-2")}>
                {label}
            </span>
        </button>;
}
const APP_BAR_ITEM_CLASS = "flex w-[58px] min-w-0 flex-col items-center gap-1 rounded-xl pb-0.5 pt-0.5";
const APP_BAR_ICON_CLASS = "flex h-[30px] w-[30px] items-center justify-center overflow-hidden";
const APP_BAR_LABEL_CLASS = "max-w-full truncate text-caption leading-tight";
export function AppBarItemSkeleton({
  icon,
  sizeLabel
}: {
  icon: ReactNode;
  sizeLabel: string;
}) {
  return <span className={APP_BAR_ITEM_CLASS} data-sentry-component="AppBarItemSkeleton" data-sentry-source-file="AppBar.tsx">
            <span className={APP_BAR_ICON_CLASS}>{icon}</span>
            <SkeletonText className={APP_BAR_LABEL_CLASS} data-sentry-element="SkeletonText" data-sentry-source-file="AppBar.tsx">
                {sizeLabel}
            </SkeletonText>
        </span>;
}
export function AppBarMoreIcon() {
  return <span className="flex h-[30px] w-[30px] items-center justify-center rounded-field bg-offwhite-2 text-ink" data-sentry-component="AppBarMoreIcon" data-sentry-source-file="AppBar.tsx">
            <Glyph name="more" className="h-4 w-4" data-sentry-element="Glyph" data-sentry-source-file="AppBar.tsx" />
        </span>;
}
export function AppGrid({
  label,
  children
}: {
  label?: string;
  children: ReactNode;
}) {
  return <div role="group" aria-label={label} className="grid grid-cols-4 justify-items-center gap-y-3 px-2 pt-3" data-sentry-component="AppGrid" data-sentry-source-file="AppBar.tsx">
            {children}
        </div>;
}